import React, { useCallback, useEffect } from 'react';
import { Box, Button, Container, Flex, Heading, Stack, Text, useToast } from '@chakra-ui/react';
import { Global } from '@emotion/react';
import { useQuery, useMutation } from 'react-query';
import colors from './onboarding-colors';
import {
  fetchUserData,
  createNewMembership,
  SignupStep,
  SpotifyStep,
  SocialsStep,
  ImageSection,
} from './onboarding-components';
import { dataApiUrl } from 'utils/url';
import debounce from 'lodash/debounce';
import { formEvent } from 'data-client/analytics';

const INSTAGRAM = 'instagram';
const YOUTUBE = 'youtube';
const TIKTOK = 'tiktok';

const Onboarding = () => {
  const [currentStep, setCurrentStep] = React.useState('signup');
  const [show, setShow] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [spotifyId, setSpotifyId] = React.useState('');
  const [selectedSpotifyArtist, setSelectedSpotifyArtist] = React.useState(null);
  const [spotifyArtists, setSpotifyArtists] = React.useState([]);
  const [chartmetricId, setChartmetricId] = React.useState('');

  const [socials, setSocials] = React.useState({
    [INSTAGRAM]: '',
    [YOUTUBE]: '',
    [TIKTOK]: '',
  });

  const toast = useToast();

  const sessionId = new URLSearchParams(window.location.search).get('session_id');

  const { data: userData, isError: userDataError } = useQuery({
    queryKey: ['userData', sessionId],
    queryFn: () => fetchUserData(sessionId),
    enabled: !!sessionId,
    retry: 2,
  });

  const { mutate: createMembership, isLoading } = useMutation({
    mutationFn: createNewMembership,
    onSuccess: async data => {
      toast({
        title: 'Account created successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      localStorage.setItem('scale_music_auth0_token', data?.access_token);
      sessionStorage.setItem('signup_success', 'true');

      formEvent('sign-up', 'success', { form_page: 'onboarding' });

      setTimeout(async () => {
        window.location.href = process.env.REACT_APP_VENICE_URL;
      }, 1000);
    },
    onError: error => {
      toast({
        title: 'Error creating account',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      formEvent('sign-up', 'error', { error_message: error.message });
    },
  });

  const handleClickShow = () => setShow(!show);

  const validatePassword = password => {
    if (!password) {
      return 'Password is required';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/\d/.test(password)) {
      return 'Password must contain at least one digit';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleNext = () => {
    if (currentStep === 'signup') {
      const passwordError = validatePassword(password);
      if (passwordError) {
        toast({
          title: passwordError,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setCurrentStep('connect_spotify');
    } else if (currentStep === 'connect_spotify') {
      setCurrentStep('input_socials');
    } else {
      createMembership({
        stripeSessionId: sessionId,
        password,
        spotifyArtist: selectedSpotifyArtist,
        socials,
        chartmetricId,
      });
    }
  };

  const handleSelectSpotifyArtist = artist => {
    setSelectedSpotifyArtist(artist);
    setSpotifyId('');
  };

  const handleChangeSelectedSpotifyArtist = () => {
    setSelectedSpotifyArtist(null);
    setSpotifyId('');
    setChartmetricId('');
    setSocials({
      [INSTAGRAM]: '',
      [YOUTUBE]: '',
      [TIKTOK]: '',
    });
    setCurrentStep('connect_spotify');
  };

  // Debounce the function to limit API calls
  const fetchArtists = useCallback(
    debounce(async id => {
      try {
        const response = await fetch(`${dataApiUrl()}/spotify/search?query=${id}&types[]=artist`);
        const data = await response.json();
        const artists = data?.data?.artists?.items.map(artist => ({
          id: artist.id,
          name: artist.name,
          imageUrl: artist?.images?.length > 0 ? artist.images[0].url : null,
        }));
        setSpotifyArtists(artists);
      } catch (error) {
        console.error('Error fetching artists:', error);
      }
    }, 500), // 500ms debounce delay
    []
  );

  const fetchChartmetricArtist = useCallback(async spotifyId => {
    try {
      const response = await fetch(`${dataApiUrl()}/chartmetric/artist-urls/${spotifyId}`);
      const data = await response.json();
      const chartmetricId = data?.data?.chartmetricId;
      const urls = data?.data?.urls;

      setChartmetricId(chartmetricId?.toString());

      if (urls?.length > 0) {
        const urls = data?.data?.urls;

        const instagramUrl = urls.find(url => url.domain === INSTAGRAM)?.url[0];
        const youtubeUrl = urls.find(url => url.domain === YOUTUBE)?.url[0];
        const tiktokUrl = urls.find(url => url.domain === TIKTOK)?.url[0];

        setSocials({
          [INSTAGRAM]: instagramUrl || '',
          [YOUTUBE]: youtubeUrl || '',
          [TIKTOK]: tiktokUrl || '',
        });
      }
    } catch (error) {
      console.error('Error fetching chartmetric artists:', error);
    }
  }, []);

  useEffect(() => {
    if (spotifyId) {
      fetchArtists(spotifyId);
    }
  }, [spotifyId, fetchArtists]);

  useEffect(() => {
    if (selectedSpotifyArtist?.id) {
      fetchChartmetricArtist(selectedSpotifyArtist.id);
    }
  }, [selectedSpotifyArtist, fetchChartmetricArtist]);

  if (userDataError) {
    return (
      <Box p={8}>
        <Text>Error loading user data. Please try again later.</Text>
      </Box>
    );
  }

  return (
    <>
      <Global
        styles={`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Manrope:wght@400;500;600;700&display=swap');
        `}
      />

      <Flex minH="100vh" width="full" direction={{ base: 'column', md: 'row' }} bg={colors.brown[100]}>
        <Box flex="3" display="flex" justifyContent="center" alignItems="center" mb="6rem" minW="33rem">
          <Container maxWidth="container.sm">
            <Stack spacing={8} align="flex-start" width="100%" px="4rem">
              <Stack spacing={3} width="100%">
                <Heading
                  fontSize="2rem"
                  fontWeight="medium"
                  fontFamily="Playfair Display, serif"
                  color={colors.text.primary}
                  lineHeight="120%">
                  <Text color={colors.text.secondary}>Hey, {userData?.name}</Text>
                  Welcome to Venice Music.
                </Heading>
                <Text
                  fontSize="1.125rem"
                  color={colors.text.primary}
                  mt={2}
                  fontFamily="Manrope, sans-serif"
                  fontWeight="400"
                  lineHeight="130%">
                  Let's get to know more about you so we can set expectations and personalize your strategy.
                </Text>
              </Stack>

              <Box
                bg={colors.brown[300]}
                p="1.5rem"
                borderRadius="1rem"
                boxShadow="sm"
                width="100%"
                maxWidth="25rem"
                mx="auto" // Center the box horizontally
              >
                {currentStep === 'signup' && (
                  <SignupStep
                    show={show}
                    handleClick={handleClickShow}
                    password={password}
                    setPassword={setPassword}
                    email={userData?.email}
                  />
                )}

                {currentStep === 'connect_spotify' && (
                  <SpotifyStep
                    spotifyId={spotifyId}
                    setSpotifyId={setSpotifyId}
                    selectedSpotifyArtist={selectedSpotifyArtist}
                    handleSelectSpotifyArtist={handleSelectSpotifyArtist}
                    handleChangeSelectedSpotifyArtist={handleChangeSelectedSpotifyArtist}
                    spotifyArtists={spotifyArtists}
                    handleNext={handleNext}
                  />
                )}

                {currentStep === 'input_socials' && (
                  <SocialsStep
                    socials={socials}
                    setSocials={setSocials}
                    selectedSpotifyArtist={selectedSpotifyArtist}
                    handleChangeSelectedSpotifyArtist={handleChangeSelectedSpotifyArtist}
                  />
                )}

                {userData?.email && (
                  <Button
                    mt={6}
                    bg={colors.button.background}
                    borderRadius="2rem"
                    color={colors.white}
                    _hover={{ bg: colors.button.backgroundHover }}
                    _active={{ bg: colors.button.backgroundActive }}
                    onClick={handleNext}
                    isLoading={isLoading}>
                    Next
                  </Button>
                )}
              </Box>
            </Stack>
          </Container>
        </Box>
        <ImageSection />
      </Flex>
    </>
  );
};

export default Onboarding;
